import axios from "axios";
import axiosInst from "api/config/axiosConfig";
import { newDiagnosticId, recordDiagnostic, reportError } from "utils/diagnostics";

export const approveFirmwareBundle = async (softwareVersion: string, modemFirmware: string): Promise<void> => {
  await axiosInst.post("/beta/appSystemInfo?manage=bundle", { softwareVersion, modemFirmware });
};

export interface ManagedSoftwareVersion {
  softwareVersionID: number;
  swVersion: string;
  portalVersion: string;
  allowDownload: boolean;
  allowVpn: boolean;
  approvedSwVersion: string;
}

export interface SoftwareVersionFlags {
  softwareVersionId: number;
  allowDownload: boolean;
  allowVpn: boolean;
}

export const getManagedSoftwareVersions = async (): Promise<ManagedSoftwareVersion[]> => {
  const response = await axiosInst.get("/beta/appSystemInfo?swVersion=manage");
  return response.data ?? [];
};

export const setSoftwareVersionFlags = async (body: SoftwareVersionFlags): Promise<void> => {
  await axiosInst.put("/beta/appSystemInfo?manage=version", body);
};

export const deleteSoftwareVersion = async (softwareVersionId: number): Promise<void> => {
  await axiosInst.delete(`/beta/appSystemInfo?softwareVersionId=${softwareVersionId}`);
};

export interface RegisterVersionRequest {
  model: string;
  version: string;
  fileName: string;
  targetBand?: string;
  sha256: string;
  allowDownload: boolean;
  allowVpn: boolean;
}

export interface ManagedModemFirmware {
  eiSoftwareImageId: number;
  fileName: string;
  downloadPath: string;
  targetBand: string;
  model: string;
  current: boolean;
}

export const getManagedModemFirmware = async (): Promise<ManagedModemFirmware[]> => {
  const response = await axiosInst.get("/beta/appSystemInfo?swVersion=manageModem");
  return response.data ?? [];
};

export const getFirmwareUploadUrl = async (fileName: string): Promise<{ uploadUrl: string; s3Key: string }> => {
  const response = await axiosInst.post("/beta/appSystemInfo?manage=uploadUrl", { fileName });
  return response.data;
};

// Bare axios: the presigned URL carries its own auth, so the app interceptor's
// Authorization header must not be attached to the S3 PUT.
export const uploadFirmwareToS3 = async (uploadUrl: string, file: File): Promise<void> => {
  const started = Date.now();
  const fields = { operation: "software.upload_firmware", method: "PUT", request_id: newDiagnosticId() };
  recordDiagnostic("operation.started", fields);
  try {
    const response = await axios.put(uploadUrl, file);
    recordDiagnostic("operation.completed", { ...fields, status: response.status, duration_ms: Date.now() - started });
  } catch (error) {
    reportError(fields.operation, error, { ...fields, duration_ms: Date.now() - started });
    throw error;
  }
};

export const registerSoftwareVersion = async (body: RegisterVersionRequest): Promise<void> => {
  await axiosInst.post("/beta/appSystemInfo?manage=registerVersion", body);
};
